import { useId } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

import { VariableAutocomplete } from '../VariableAutocomplete';
import { getWhatsAppFallbackErrors } from '../whatsapp-fallback';

import type { VariableAutocompleteProps } from '../VariableAutocomplete';
import type { SendSmsNodeData } from '@repo/shared-types';

type VariableContext = Pick<
  VariableAutocompleteProps,
  | 'responseFieldsSchema'
  | 'reviewNodeMetas'
  | 'classifierNodeMetas'
  | 'qualityNodeMetas'
  | 'agentNodeMetas'
  | 'analyticsNodeMetas'
>;
interface Props extends VariableContext {
  data: SendSmsNodeData;
  onChange: (data: SendSmsNodeData) => void;
}

const fields = [
  {
    key: 'from',
    label: 'SMS sender',
    hint: 'An SMS-capable sender for the selected provider, in international format. Variables are supported.',
  },
  {
    key: 'body',
    label: 'SMS message',
    hint: 'Use the existing vehicle and payment link variables. This message is saved for fallback.',
  },
  {
    key: 'key',
    label: 'Unique request/car key',
    hint: 'Use stable variables identifying this request and selected car. Reusing a key for the same recipient suppresses repeat sends.',
  },
  {
    key: 'consent',
    label: 'Consent expression',
    hint: 'Must resolve to true before either message can be sent. Use verified customer consent; opt-outs are still respected.',
  },
] as const;

export const WhatsAppFallbackFields = ({ data, onChange, ...variableContext }: Props) => {
  const id = useId();
  const fallback = data.waSmsFallback;
  const supported =
    data.provider === 'whatsapp' && (data.waMessageType ?? 'template') === 'template';
  if (!supported && !fallback) return null;
  const errors = getWhatsAppFallbackErrors(data);

  return (
    <section
      aria-label="Automatic SMS fallback settings"
      className="space-y-3 rounded-md border p-3"
    >
      <div className="flex min-h-11 items-center justify-between gap-3">
        <label htmlFor={`${id}-enabled`} className="cursor-pointer text-sm font-medium">
          Automatic SMS fallback
        </label>
        <Switch
          id={`${id}-enabled`}
          checked={!!fallback}
          disabled={!supported && !fallback}
          aria-describedby={`${id}-help`}
          onCheckedChange={(enabled) =>
            onChange({
              ...data,
              waSmsFallback: enabled
                ? { provider: 'twilio', from: '', body: '', key: '', consent: '' }
                : undefined,
            })
          }
        />
      </div>
      <p id={`${id}-help`} className="text-muted-foreground text-xs leading-relaxed">
        Off: this node sends only WhatsApp. For a separate SMS node, leave this off and connect the
        Failed output to that node. On: this node can also send SMS after a confirmed failure to
        send WhatsApp or a later confirmed delivery failure, without using the Failed connection for
        that fallback. Pending or unknown results do not trigger automatic SMS.
      </p>
      {fallback && (
        <>
          <p className="text-xs leading-relaxed">
            Do not combine automatic fallback with a separate SMS fallback path. Turning this off
            does not cancel messages already in progress.
          </p>
          {errors.length > 0 && (
            <div role="alert" className="text-destructive space-y-1 text-xs">
              {errors.map((error) => (
                <p key={error}>{error}</p>
              ))}
            </div>
          )}
          <div className="space-y-1">
            <label htmlFor={`${id}-provider`} className="text-sm font-medium">
              Fallback SMS provider
            </label>
            <Select
              value={fallback.provider}
              onValueChange={(provider) => {
                if (provider === 'twilio' || provider === 'telnyx')
                  onChange({ ...data, waSmsFallback: { ...fallback, provider } });
              }}
            >
              <SelectTrigger id={`${id}-provider`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="twilio">Twilio</SelectItem>
                <SelectItem value="telnyx">Telnyx</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {fields.map(({ key, label, hint }) => (
            <div key={key} className="min-w-0 space-y-1">
              <label htmlFor={`${id}-${key}`} className="text-sm font-medium">
                {label}
              </label>
              <VariableAutocomplete
                {...variableContext}
                id={`${id}-${key}`}
                value={fallback[key]}
                multiline={key === 'body'}
                rows={4}
                onChange={(value) =>
                  onChange({ ...data, waSmsFallback: { ...fallback, [key]: value } })
                }
              />
              <p className="text-muted-foreground text-xs leading-relaxed">{hint}</p>
            </div>
          ))}
        </>
      )}
    </section>
  );
};
